


































import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  PropType,
} from '@vue/composition-api';
import { Button } from '@hb/coffee';
import ChannelContract from '@/widgets/contract-view/channel-contract/index.vue';
import type { ContractListResponse, ChannelContractInfo } from '@/widgets/contract-view/type';
import { dcCreditSceneEnum } from '@/api/preApplyChoose/types';
import PreApplyChooseAPI from '@/api/preApplyChoose';
import { UserInfoStatus } from '@/enums/UserInfoStatus';
import { NewVersionEnum } from '@/enums/newVersionRoute';
import { jumpToNewVersion } from '@/shared/utils/jumpTo';
import { ubtSendError } from '@/shared/utils/ubt';
import { session } from '@/shared/utils/storage';

const USER_HAS_JUMPED_OUT_PAGE_KEY = 'userHasJumpedOutPage';

export default defineComponent({
  name: 'DcContract',
  components: {
    ChannelContract,
    Button,
  },
  props: {
    /** 渠道列表（基础类型） */
    dcChannelList: {
      type: Array as PropType<ContractListResponse[]>,
      required: true,
    },
    /** 贷超授信场景 */
    dcCreditScene: {
      type: String as PropType<dcCreditSceneEnum>,
      required: true,
    },
    /** 申请模式 */
    applyMode: {
      type: String,
      default: '',
    },
  },
  setup(props, context) {
    const store = context.root.$store;
    /** 本地渠道列表（添加选中状态） */
    const localChannelList = ref<ChannelContractInfo[]>([]);

    /** 倒计时秒数 */
    const countdown = ref(5);
    /** 倒计时定时器 */
    let countdownTimer: number | null = null;

    /** 初始化渠道列表，添加选中状态 */
    const initChannelList = () => {
      localChannelList.value = props.dcChannelList.map((channel) => ({
        ...channel,
        isSelected: true, // 默认全部选中
      }));
    };

    /** 处理渠道选择 - 内部处理，不向外抛出 */
    const handleSelectChannel = (
      { productId, isSelected }: { productId: string; isSelected?: boolean },
    ) => {
      const channel = localChannelList.value.find((c) => c.productId === productId);
      if (channel) {
        // 内部切换选中状态
        channel.isSelected = !isSelected;
      }
    };

    /** 处理协议切换 - 内部处理，不向外抛出 */
    const handleChangeContract = (payload: any) => {
      // 可以在这里添加埋点等逻辑
    };

    /** 处理渠道展开 - 内部处理，不向外抛出 */
    const handleExpandChannel = (payload: { productId: string }) => {
      // 可以在这里添加埋点等逻辑
    };

    /** 按钮是否禁用 */
    const isButtonDisabled = computed(() => countdown.value > 0);

    /** 按钮文案 */
    const buttonText = computed(() => {
      if (countdown.value > 0) {
        return `已阅读并同意协议（${countdown.value}s）`;
      }
      return '已阅读并同意协议';
    });

    /** 选中的渠道ID列表 */
    const selectedChannelIds = computed(() => localChannelList.value
      .filter((channel) => channel.isSelected)
      .map((channel) => Number(channel.productId)));

    /**
     * 启动倒计时
     */
    const startCountdown = () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
      countdown.value = 5;
      countdownTimer = window.setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          if (countdownTimer) {
            clearInterval(countdownTimer);
            countdownTimer = null;
          }
        }
      }, 1000);
    };

    /**
     * 跳转到结果页
     */
    const jumpToResultPage = (taskId: string) => {
      /** 与熟悉页面逻辑保持一致 */
      session.set(USER_HAS_JUMPED_OUT_PAGE_KEY, 1);
      jumpToNewVersion(
        NewVersionEnum.HALF_PROCESS_RESULT,
        true,
        `taskId=${taskId}&needCustomGoBack=true&source=${props.dcCreditScene}`,
      );
    };

    /**
     * 跳转到失败页
     */
    const jumpToFailedPage = (errorMessage: string) => {
      jumpToNewVersion(NewVersionEnum.HALF_PROCESS_FAILED, true);
      ubtSendError(errorMessage);
    };

    /**
     * 处理申请按钮点击
     */
    const handleApply = async () => {
      try {
        // 获取用户资料完整度
        const userProgress = store.state.userProfileProgress?.userInfoProgress;
        const fillInTheInformation = userProgress?.fillInTheInformation;

        // 调用贷超授信接口
        const { taskId } = await PreApplyChooseAPI.dcCreditApply({
          commodityList: selectedChannelIds.value,
          scene: props.dcCreditScene as dcCreditSceneEnum,
          applyMode: props.applyMode,
          incompleteInfo: fillInTheInformation !== UserInfoStatus.Completed,
        });

        // 跳转到结果页
        jumpToResultPage(taskId);
      } catch (error) {
        // 跳转到失败页
        jumpToFailedPage('dcCreditApply error');
      }
    };

    /** 监听 props.channelList 变化 */
    watch(
      () => props.dcChannelList,
      () => {
        initChannelList();
      },
      { deep: true },
    );

    onMounted(() => {
      initChannelList();
      startCountdown();
    });

    onBeforeUnmount(() => {
      // 清理定时器
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
    });

    return {
      localChannelList,
      isButtonDisabled,
      buttonText,
      handleSelectChannel,
      handleChangeContract,
      handleExpandChannel,
      handleApply,
    };
  },
});
